import { useState } from 'react';
import Button from '../../components/base/Button';
import Header from '../../components/feature/Header';

const PitchPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    companyName: '',
    region: '',
    stage: '',
    field: '',
    businessModel: '',
    elevatorPitch: '',
    deckLink: ''
  });
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const regions = [
    'North America',
    'Latin America',
    'Europe',
    'Asia Pacific',
    'Middle East & Africa',
    'Global'
  ];

  const stages = [
    'Pre-Seed',
    'Seed',
    'Series A',
    'Series B',
    'Series C+',
    'Growth Stage'
  ];

  const fields = [
    'FinTech',
    'HealthTech',
    'EdTech',
    'E-commerce',
    'SaaS',
    'Marketplace',
    'AI/ML',
    'Blockchain',
    'IoT',
    'CleanTech',
    'AgTech',
    'PropTech',
    'Other'
  ];

  const businessModels = [
    'B2B',
    'B2C',
    'B2B2C',
    'Marketplace'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAttachment(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const submitData = new FormData();
      
      // Add form fields
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      // Add attachment if present
      if (attachment) {
        submitData.append('deckAttachment', attachment);
      }

      const response = await fetch('https://readdy.ai/api/form/d49k9ph7bl57tkq9h6sg', {
        method: 'POST',
        body: submitData
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          name: '',
          email: '',
          companyName: '',
          region: '',
          stage: '',
          field: '',
          businessModel: '',
          elevatorPitch: '',
          deckLink: ''
        });
        setAttachment(null);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
              Pitch to EULA VC
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              We're always looking for exceptional founders building the next generation of innovative companies. 
              If you're working on something groundbreaking, we'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* What We Look For Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-12 text-center" style={{ fontFamily: '"Playfair Display", serif' }}>
            What We Look For
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold mb-4 text-teal-600">Exceptional Founders</h3>
              <p className="text-gray-700 leading-relaxed">
                We back founders with deep domain expertise, unique insights, and the drive to build category-defining companies.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold mb-4 text-teal-600">Large Markets</h3>
              <p className="text-gray-700 leading-relaxed">
                We invest in companies addressing large, growing markets with the potential for significant scale.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold mb-4 text-teal-600">Innovative Technology</h3>
              <p className="text-gray-700 leading-relaxed">
                We look for companies leveraging technology to create differentiated products and solutions.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold mb-4 text-teal-600">Sustainable Advantage</h3>
              <p className="text-gray-700 leading-relaxed">
                We seek businesses with strong defensibility and the potential for network effects.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Process Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-12 text-center" style={{ fontFamily: '"Playfair Display", serif' }}>
            Application Process
          </h2>
        </div>
      </section>

      {/* Submit Your Pitch Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-12 text-center" style={{ fontFamily: '"Playfair Display", serif' }}>
            Submit Your Pitch
          </h2>
          <div className="bg-gray-50 p-8 md:p-12 rounded-xl shadow-sm">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                Pitch Us!
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                We review every pitch submitted and aim to respond within 10 business days. While we can't offer 
                individual feedback, each submission is carefully evaluated by our investment team.
              </p>
            </div>

            <form 
              className="space-y-6"
              action="https://readdy.ai/api/form/d49kdrv020ubo1989v90"
              method="POST"
              encType="multipart/form-data"
              data-readdy-form
              onSubmit={handleSubmit}
            >
              <input type="hidden" name="recipient_email" value="eu.la.vccompany@gmail.com" />
              
              {/* Name & Email Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-900 mb-2">
                    Name & Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  />
                </div>
              </div>

              {/* Company Name */}
              <div>
                <label htmlFor="companyName" className="block text-sm font-semibold text-slate-900 mb-2">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                />
              </div>

              {/* Dropdowns Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="region" className="block text-sm font-semibold text-slate-900 mb-2">
                    Region
                  </label>
                  <div className="relative">
                    <select
                      id="region"
                      name="region"
                      value={formData.region}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors appearance-none bg-white cursor-pointer"
                    >
                      <option value="">Select Region</option>
                      {regions.map(region => (
                        <option key={region} value={region}>{region}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="stage" className="block text-sm font-semibold text-slate-900 mb-2">
                    Stage <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="stage"
                      name="stage"
                      value={formData.stage}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors appearance-none bg-white cursor-pointer"
                    >
                      <option value="">Select Stage</option>
                      {stages.map(stage => (
                        <option key={stage} value={stage}>{stage}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>
              </div>

              {/* Field and Business Model Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="field" className="block text-sm font-semibold text-slate-900 mb-2">
                    Field <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="field"
                      name="field"
                      value={formData.field}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors appearance-none bg-white cursor-pointer"
                    >
                      <option value="">Select Field</option>
                      {fields.map(field => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="businessModel" className="block text-sm font-semibold text-slate-900 mb-2">
                    B2B or B2C <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="businessModel"
                      name="businessModel"
                      value={formData.businessModel}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors appearance-none bg-white cursor-pointer"
                    >
                      <option value="">Select Business Model</option>
                      {businessModels.map(model => (
                        <option key={model} value={model}>{model}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>
              </div>

              {/* Elevator Pitch */}
              <div>
                <label htmlFor="elevatorPitch" className="block text-sm font-semibold text-slate-900 mb-2">
                  Elevator Pitch <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="elevatorPitch"
                  name="elevatorPitch"
                  value={formData.elevatorPitch}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors resize-vertical"
                  placeholder="Describe your company, the problem you're solving, and your unique solution..."
                />
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.elevatorPitch.length}/500 characters
                </div>
              </div>

              {/* Deck Attachment */}
              <div>
                <label htmlFor="deckAttachment" className="block text-sm font-semibold text-slate-900 mb-2">
                  Deck Attachment
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  Please include either deck or link to deck for a valid submission.
                </p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-teal-400 transition-colors">
                  <input
                    type="file"
                    id="deckAttachment"
                    name="deckAttachment"
                    onChange={handleFileChange}
                    accept=".pdf,.ppt,.pptx,.doc,.docx"
                    className="hidden"
                  />
                  <label htmlFor="deckAttachment" className="cursor-pointer">
                    <div className="flex flex-col items-center">
                      <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-2"></i>
                      <span className="text-sm font-medium text-gray-700">
                        {attachment ? attachment.name : 'Click to upload or drag and drop'}
                      </span>
                      <span className="text-xs text-gray-500 mt-1">
                        PDF, PPT, PPTX, DOC, DOCX (max 10MB)
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Deck Link */}
              <div>
                <label htmlFor="deckLink" className="block text-sm font-semibold text-slate-900 mb-2">
                  Deck Link
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  Please include either deck or link to deck for a valid submission.
                </p>
                <input
                  type="url"
                  id="deckLink"
                  name="deckLink"
                  value={formData.deckLink}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="https://..."
                />
              </div>

              {/* Submit Button */}
              <div className="text-center pt-6">
                {submitStatus === 'success' && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 font-medium">
                      Thank you for your submission! We'll review your pitch and get back to you within 10 business days.
                    </p>
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 font-medium">
                      There was an error submitting your pitch. Please try again.
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={isSubmitting}
                  className="px-12 py-4 text-lg whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Submitting...
                    </>
                  ) : (
                    'Submit Pitch'
                  )}
                </Button>
                
                <p className="text-sm text-gray-500 mt-4">
                  Do not submit passwords through this form.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Questions About the Process Section */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
            Questions About the Process?
          </h2>
        </div>
      </section>
    </div>
  );
};

export default PitchPage;
